import { createSlice, current } from "@reduxjs/toolkit";
import { union, has, isEmpty } from "lodash";
import { PERMISSIONS, ROLES } from "../../permissions/permissions-map";
import getNumericValue from "../../utils/getNumericValue";
import { sortTypeOptions } from "../../utils";
import { groupBy } from "../../pages/ManagePlanograms/DepartmentSearch";

export const USER_TYPES = ["Team", "Individual"];

export const initialState = {
  loading: false,
  dashboardFilters: {
    memberNumber: "",
    storeName: "",
    city: "",
    state: "",
    statusCode: "",
    projectTypeId: "",
    planner: "",
    mac: "",
    startDate: "",
    endDate: "",
  },
  itemFilters: {
    filterBy: "",
    filterValue: "",
    startDate: "",
    endDate: "",
    sortBy: 1,
    sortType: sortTypeOptions[0],
  },
  pogFilters: {
    filterBy: "",
    filterValue: "",
    startDate: "",
    endDate: "",
    sortBy: 0,
    sortType: sortTypeOptions[0],
    _sku: null,
    _departmentLetter: null,
    _planogramNumber: null,
    _vendor: null,
  },
  departmentFilters: {
    viewBy: groupBy[0],
    department: null,
    category: null,
    planogram: null,
    planogramValue: null,
    vendorName: null,
    skuValue: null,
    _deptLetter: null,
    _pogName: null,
    _sku: null,
    _prevViewBy: "",
  },
  total: {
    totalPogs: 0,
    selected: 0,
    existing: 0,
    lf: 0,
    adjLf: 0,
    skuQty: 0,
    adjCost: 0.0,
    extCost: 0.0,
  },
  notification: {
    open: false,
    message: "",
    severity: "success" /* error | warning | info */,
    duration: 8000,
  },
  user: {
    id: "",
    type: USER_TYPES[0],
    name: "",
    uname: "",
    picture: "",
    email: "",
    role: ROLES.guest,
    permissions: PERMISSIONS[ROLES.guest],
  },
  store: {
    id: "",
    memberNumber: "",
    name: "",
    creditLimit: "",
    pending: false,
    availableCreditLimit: "",
    distributionCenters: [],
    rdcNumber: -1,
    rdc: "",
    isArchived: false,
    isCompleted: false,
    dataguideInProgress: false,
    completedOrderCount: -1,
    numberOfItemsPresent: -1,
    numberOfReleasedItemsPresent: -1,
    isFillInOrder: false,
    orderTypeId: null,
    isStoreSupplies: false,
  },
  report: {
    headers: [],
  },
  authState: {
    isAuthenticated: false,
    isLogout: false,
    code: null,
    expireDateTime: null,
    id_token: null,
    access_token: null,
    refresh_token: null,
  },
  offlineDetails: {
    isServerUp: false,
  },
  settings: {
    entityType: "",
    restrictedPogs: {},
  },
  ssItemPogSummary: {
    Selected: "N",
  },
  adjQtyError: null,
  prevAdjQty: null,
  adjQtyScope: null,
};

export const globalSlice = createSlice({
  name: "global",
  initialState,
  reducers: {
    setStoreDetails: (state, { payload }) => {
      const store = state.store;
      const rdcList = current(state)?.store.distributionCenters || [];
      store.id = payload?.id ?? "";
      store.memberNumber = payload?.memberNumber ?? "";
      store.name = payload?.name ?? "";
      store.creditLimit = payload?.creditLimit ?? "";
      store.pending = Boolean(payload?.pending) ?? false;
      store.rdcNumber = payload?.rdcNumber ?? -1;
      if (store.rdcNumber && rdcList.length) {
        const selectedRdc =
          rdcList.find((rdc) => rdc.ConfigKey === store.rdcNumber) ?? {};
        store.rdc = !isEmpty(selectedRdc) ? selectedRdc.ConfigValue : "";
      }
      store.isArchived = Boolean(payload?.isArchived) ?? false;
      store.isCompleted = payload?.isCompleted ?? false;
      store.numberOfItemsPresent = payload?.numberOfItemsPresent ?? -1;
      store.numberOfReleasedItemsPresent =
        payload?.numberOfReleasedItemsPresent ?? -1;
      store.isFillInOrder = payload?.isFillInOrder || store.isFillInOrder;
      store.orderTypeId = payload?.orderTypeId || null;
      store.isStoreSupplies = payload?.orderTypeId === 6;
      if (!store.id) {
        store.isFillInOrder = false;
        store.completedOrderCount = -1;
        store.orderTypeId = null;
        store.isStoreSupplies = false;
      }
    },
    setOrderTypeDetails: (state, { payload }) => {
      const store = state.store;
      store.numberOfItemsPresent = payload?.numberOfItemsPresent ?? -1;
      store.numberOfReleasedItemsPresent =
        payload?.numberOfReleasedItemsPresent ?? -1;
    },
    setFillInOrder: (state, { payload }) => {
      const store = state.store;
      store.isFillInOrder = payload || false;
    },
    setSSPogItemSummary: (state, { payload }) => {
      const ssItemPogSummary = state.ssItemPogSummary;
      ssItemPogSummary.Selected = payload?.Selected || "N";
    },
    setCompletedOrderCount: (state, { payload }) => {
      const store = state?.store;
      store.completedOrderCount = payload ?? -1;
    },
    setDistributionCenters: (state, { payload }) => {
      const store = state.store;
      store.distributionCenters = payload ?? [];
    },
    setDataguideInProgress: (state, { payload }) => {
      const store = state.store;
      store.dataguideInProgress = payload ?? false;
    },
    setAdjQtyError: (state, { payload }) => {
      state.adjQtyError = payload ?? null;
    },
    setPrevAdjQty: (state, { payload }) => {
      state.prevAdjQty = payload ?? null;
    },
    setUserType: (state, { payload }) => {
      const user = state.user;
      user.type = payload;
    },
    setUserDetails: (state, { payload }) => {
      const user = state.user;
      user.name = payload.name ?? "";
      user.uname = payload.uname ?? "";
      user.picture = payload.picture ?? "";
      user.id = payload.id ?? "";
      user.email = payload.email ?? "";
    },
    setUserRole: (state, { payload }) => {
      const user = state.user;
      //Note: if no role provided then it will set to 'guest' role.
      const currentRole = !!payload ? payload : ROLES.guest;
      user.role = currentRole;
      user.permissions = PERMISSIONS[currentRole];
    },
    setReportHeaders: (state, { payload }) => {
      const report = state.report;
      report.headers = payload ?? [];
    },
    setGlobalLoader: (state, { payload }) => {
      state.loading = payload;
    },
    showNotifier: (state, { payload }) => {
      const notification = state.notification;
      if (payload) {
        notification.open = !!payload.message;
        notification.message = payload.message;
        notification.severity = payload.severity;
      } else {
        notification.open = initialState.notification.open;
        notification.message = initialState.notification.message;
        notification.severity = initialState.notification.severity;
      }
    },
    calculateAvailableCreditLimit: (state, { payload }) => {
      const store = state.store;
      store.availableCreditLimit = getNumericValue(store.creditLimit) - payload;
    },
    setAuthState: (state, { payload }) => {
      if (!payload?.isAuthenticated) {
        state.authState = { ...initialState.authState };
      } else {
        state.authState = { ...payload };
      }
    },
    setOfflineServerStatus: (state, { payload }) => {
      const offline = state.offlineDetails;
      offline.isServerUp = payload || false;
    },
    setSettings: (state, { payload }) => {
      const settings = state.settings;
      settings.entityType = payload?.entityType ?? "";
    },
    setRestrictedPog: (state, { payload }) => {
      const { scope, pogId, clear = false } = payload;
      const settings = state.settings;
      if (!clear) {
        const currentList = has(settings.restrictedPogs, scope)
          ? settings.restrictedPogs[scope]
          : [];
        settings.restrictedPogs = {
          ...settings.restrictedPogs,
          [scope]: union(currentList, [pogId]),
        };
      } else {
        settings.restrictedPogs = initialState.settings.restrictedPogs;
      }
    },
    setItemFilters: (state, { payload }) => {
      const itemFilters = state.itemFilters;
      itemFilters[payload.field] = payload.value;
    },
    resetItemFilters: (state) => {
      const itemFilters = state.itemFilters;
      itemFilters.filterBy = "";
      itemFilters.filterValue = "";
      itemFilters.startDate = "";
      itemFilters.endDate = "";
      itemFilters.sortBy = initialState.itemFilters.sortBy;
      itemFilters.sortType = sortTypeOptions[0];
    },
    setPogFilters: (state, { payload }) => {
      const pogFilters = state.pogFilters;
      pogFilters[payload.field] = payload.value;
      if (payload?.prevViewBy) {
        pogFilters._prevViewBy = payload?.prevViewBy;
      }
      if (payload?.skuFilter) {
        const otherValues = payload?.value?.split("-");
        pogFilters._departmentLetter = otherValues[0];
        pogFilters._planogramNumber = otherValues[1];
        pogFilters._sku = otherValues[2];
      }
      if (payload?.vendorFilter) {
        pogFilters._vendor = payload.value;
      }
    },
    resetPogFilters: (state, { payload }) => {
      const pogFilters = state.pogFilters;
      // To set value explicitly to any other value as required.
      if (payload?.set) {
        pogFilters[payload.set.key] = payload.set.value;
      } else {
        pogFilters.filterBy = "";
        pogFilters.filterValue = "";
        pogFilters.startDate = "";
        pogFilters.endDate = "";
        pogFilters.sortBy = 0;
        pogFilters.sortType = sortTypeOptions[0];
        pogFilters._departmentLetter = null;
        pogFilters._planogramNumber = null;
        pogFilters._sku = null;
        pogFilters._vendor = null;
      }
      // For filter reset action.
      if (payload?.allReset) {
        pogFilters._prevViewBy = "";
      }
    },
    setDepartmentFilters: (state, { payload }) => {
      const departmentFilters = state.departmentFilters;
      departmentFilters[payload?.field] = !String(payload?.value).trim().length
        ? null
        : payload?.value;
    },
    resetDepartmentFilters: (state) => {
      const departmentFilters = state.departmentFilters;
      departmentFilters.viewBy = groupBy[0];
      departmentFilters.department = null;
      departmentFilters.category = null;
      departmentFilters.planogram = null;
      departmentFilters.planogramValue = null;
      departmentFilters.vendorName = null;
      departmentFilters.skuValue = null;
      departmentFilters._deptLetter = null;
      departmentFilters._pogName = null;
      departmentFilters._sku = null;
    },
    setDashboardFilters: (state, { payload }) => {
      const dashboardFilters = state.dashboardFilters;
      dashboardFilters[payload.field] = !String(payload.value).trim().length
        ? null
        : payload.value;
    },
    resetDashboardFilters: (state) => {
      const dashboardFilters = state.dashboardFilters;
      dashboardFilters.memberNumber = "";
      dashboardFilters.storeName = "";
      dashboardFilters.city = "";
      dashboardFilters.state = "";
      dashboardFilters.statusCode = "";
      dashboardFilters.projectTypeId = "";
      dashboardFilters.planner = "";
      dashboardFilters.mac = "";
      dashboardFilters.startDate = "";
      dashboardFilters.endDate = "";
    },
  },
});

export const {
  setStoreDetails,
  setCompletedOrderCount,
  setFillInOrder,
  setOrderTypeDetails,
  setDistributionCenters,
  setDataguideInProgress,
  setAdjQtyError,
  setPrevAdjQty,
  setUserType,
  setUserDetails,
  setUserRole,
  setReportHeaders,
  setGlobalLoader,
  showNotifier,
  calculateAvailableCreditLimit,
  setAuthState,
  setOfflineServerStatus,
  setSettings,
  setRestrictedPog,
  setItemFilters,
  resetItemFilters,
  setPogFilters,
  resetPogFilters,
  setDepartmentFilters,
  resetDepartmentFilters,
  setDashboardFilters,
  resetDashboardFilters,
  setSSPogItemSummary,
} = globalSlice.actions;

export default globalSlice.reducer;
